import type { CaseCopy } from "./types";

export const roadTraffic: CaseCopy = {
  slug: "road-traffic",
  displayName: "Road Traffic Fines — Italian Municipal Collections",
  issueNumberRoman: "II",
  issueNumberArabic: "02",
  mastheadIssue: "Issue II · Apr 2026",

  noteTagline: <>What follows is one such reconstruction, applied to an Italian city hall.</>,

  sourceCard: (
    <>
      The data comes from the <b>Road Traffic Fine Management</b> log, released by an Italian
      municipality and curated by the 4TU consortium. It contains{" "}
      <b>150,370 fine cases</b> and roughly 560,000 events — from the moment an officer writes
      the ticket to the moment the fine is paid, appealed, or pushed to a debt-collection agency.
      The dataset is public, anonymised, and widely used as a benchmark for process discovery on
      a high-volume administrative workflow.
    </>
  ),

  hero: {
    kicker: "Issue II  ·  The Ticket That Aged",
    title: (
      <>
        An Italian city wrote <span className="num-amber">150,370</span> fines.{" "}
        <span className="frac">Almost half</span> went to credit collection before a second notice was sent.
      </>
    ),
    dek: (
      <>
        The fines were not ignored by drivers. They were ignored by the municipality. Between
        the first notification and the handover to a debt-collection agency, the average file
        spent months sitting quiet — no reminder, no follow-up, no closed loop. The log
        reconstructs a process that was supposed to chase money and instead lost track of it.
      </>
    ),
  },

  dotGrid: {
    total: 600,
    bad: 288,
    caption: (
      <>
        <b>1 dot = ~250 fines</b>
        600 dots represent the full year of issued tickets. <em>288 dots in red</em> are the
        fines that reached credit-collection without a second notification — a routine that
        short-circuits the recovery process by design.
      </>
    ),
  },

  lede: (
    <>
      The municipal playbook is five steps long. An officer writes the fine; the office mails
      the first notice; the driver pays, or a penalty is added; a second notice goes out; and if
      nothing comes back, the file is handed to a credit collector. The logs tell a shorter,
      stranger version. The second notice is often skipped entirely. The penalty is added to a
      file no driver has yet been reminded about. By the time the letter reaches the mailbox,
      the debt has already changed hands.
    </>
  ),

  headlineFinding: {
    label: "· 01 · Headline finding",
    h2: "The city is chasing debt before it sends the second letter.",
    body: (
      <>
        The single most expensive pattern in this log is an administrative shortcut: cases that
        move from <em>Insert Fine Notification</em> directly to <em>Send for Credit Collection</em>,
        skipping the <em>Send Appeal to Prefecture</em> or a second reminder. It happens on{" "}
        <b>tens of thousands of fines per year</b> and adds, on the tail, months of dead time
        between the triggering event and the moment a debt collector picks up the file.
      </>
    ),
    issues: [
      {
        tag: "Issue I · Skipped Reminder",
        big: "48",
        unit: "%",
        ev: "Share of fines that go straight from first notification to credit-collection — without a second reminder ever leaving the office.",
      },
      {
        tag: "Issue II · Notification Lag",
        big: "82",
        unit: "d",
        ev: "Median time between fine creation and the first notification actually reaching the driver. The tail runs past one year.",
      },
      {
        tag: "Issue III · Add Penalty Loop",
        big: "41",
        unit: "k",
        ev: "Cases where 'Add penalty' is recorded more than once on the same file — roughly 27% of the year's fines. A penalty applied, reversed, reapplied — by the same office.",
      },
    ],
  },

  sectionTwo: {
    label: "· 02 · The playbook vs. the stamp log",
    diagram: {
      exhibitTag: (<>Exhibit <b>· As-declared municipal workflow</b> — with log-level reality in amber</>),
      sourceTag: "Source: DFG over 560k events",
      steps: [
        { num: "STEP 01", name: "Write",      subActs: ["Create Fine"],                     band: "teal"   },
        { num: "STEP 02", name: "Notify",     subActs: ["Send Fine", "Insert Notification"], band: "muted"  },
        { num: "STEP 03", name: "Penalise",   subActs: ["Add penalty"],                      band: "signal" },
        { num: "STEP 04", name: "Remind",     subActs: ["Appeal to Judge", "Send Appeal"],   band: "alert"  },
        { num: "STEP 05", name: "Collect",    subActs: ["Send for Credit Collection"],       band: "ink"    },
      ],
      gapDays: ["3 d", "82 d", "9 d", "2 d"],
      selfLoops: [{ fromStep: 3, label: "re-penalty" }],
      topNotes: [
        { text: (<><tspan x="274" dy="0">median <tspan className="n">82 days</tspan> before</tspan><tspan x="274" dy="18">the first notice mails</tspan></>), fromStep: 2, tickColor: "muted", width: 230, x: 260, y: 70 },
        { text: (<><tspan className="n">41,206</tspan> re-penalties — same file, again</>), fromStep: 3, tickColor: "signal", width: 230, x: 540, y: 86 },
        { text: (<><tspan x="814" dy="0">step 04 is skipped</tspan><tspan x="814" dy="18">on 48% of fines</tspan></>), fromStep: 4, tickColor: "alert", width: 220, x: 800, y: 144 },
      ],
      cancelStub: {
        label: "Send for Credit Collection · 72,178",
        fromStep: 4,
        note: (<><tspan className="n">72,178</tspan> fines → credit collector, shortcut</>),
      },
      declaredMedianDays: "96 d",
      legend: [
        { band: "teal",   label: "Write · issuance" },
        { band: "muted",  label: "Notify · stall zone" },
        { band: "signal", label: "Penalise · loop" },
        { band: "alert",  label: "Remind · often skipped" },
        { band: "ink",    label: "Collect · outcome" },
      ],
    },
    sparkline: {
      title: "Time between fine creation and first notification",
      body: (
        <>
          Most fines get a notice within weeks; a long, thin tail of files drifts past the
          <em> one-year mark</em> before the driver is ever told they owe anything.
        </>
      ),
    },
  },

  sectionThree: {
    labelFromCount: (n) => `· 03 · ${toWord(n)} findings from the log`,
  },

  sectionFive: {
    label: "· 05 · As a consultant, I'd recommend",
    h2: "Four moves. No new system. No new staff.",
    pull: "If the municipality enforces a 60-day notification ceiling, the median notification drops from 82 days to 60 on every file that exceeds the cap.",
    recs: [
      { tag: "R1", act: "Hard-cap time between fine creation and first notification at 60 days.",      impact: (<><b>−22 d</b> median notification</>) },
      { tag: "R2", act: "Require a second reminder on file before credit-collection is allowed.",       impact: (<><b>kills</b> the shortcut</>) },
      { tag: "R3", act: "Forbid re-applying a penalty on the same fine in the same quarter.",           impact: (<><b>−41k</b> penalty loops / yr</>) },
      { tag: "R4", act: "Publish a 180-day ceiling. A fine older than that is escalated to supervisor.", impact: (<><b>truncates</b> the 1-year tail</>) },
    ],
    close: (
      <>
        The fine is not the problem. The silence between the fine and the reminder is. Four
        guardrails put the silence on a clock — and the log, for the first time, stops aging in
        the drawer.
      </>
    ),
  },
};

function toWord(n: number): string {
  const words = ["zero","one","two","three","four","five","six","seven","eight","nine","ten","eleven","twelve"];
  return words[n] ?? String(n);
}
